import { rpc } from '@/api/rpc'
import { safeName, uploadToStorage } from '@/lib/files'
import { db } from '@/offline/db'
import { enqueue, flushOutbox, registerOutboxHandler } from '@/offline/outbox'
import { requestBackgroundSync } from '@/offline/sync'

export type VisitDraft = {
  clientUuid: string
  purpose: string
  companyName: string
  contactName: string
  notes: string
  lat?: number
  lng?: number
  photoIds: string[]
  visitedAt: string
}

export const PURPOSES = [
  { value: 'new_lead', label: 'New lead' },
  { value: 'design_presentation', label: 'Design presentation' },
  { value: 'follow_up', label: 'Follow-up' },
  { value: 'site_measure', label: 'Site measure' },
  { value: 'complaint', label: 'Complaint' },
  { value: 'other', label: 'Other' },
]

/** Save a site visit; it sends itself when there is signal. */
export async function queueVisit(v: VisitDraft, userId: string) {
  await enqueue({
    id: v.clientUuid,
    type: 'visit.log',
    label: `Site visit${v.companyName ? `: ${v.companyName}` : ''}`,
    userId,
    payload: { visit: v, userId },
  })
  void requestBackgroundSync()
  void flushOutbox({ userId })
}

export function registerVisitHandlers() {
  registerOutboxHandler('visit.log', async (item) => {
    const { visit: v, userId } = item.payload as { visit: VisitDraft; userId: string }
    const paths: string[] = []
    for (const id of v.photoIds) {
      const f = await db.files.get(id)
      if (!f) continue
      const path = f.uploadedPath ?? `uploads/${userId}/${v.clientUuid}/${f.id}-${safeName(f.name)}`
      if (!f.uploadedPath) {
        await uploadToStorage(path, f.blob, f.type)
        await db.files.update(f.id, { uploadedPath: path })
      }
      paths.push(path)
    }
    await rpc('log_site_visit', {
      v: {
        client_uuid: v.clientUuid,
        purpose: v.purpose,
        company_name: v.companyName,
        contact_name: v.contactName,
        notes: v.notes,
        lat: v.lat ?? null,
        lng: v.lng ?? null,
        visited_at: v.visitedAt,
        photo_paths: paths,
      },
    })
    await db.files.bulkDelete(v.photoIds)
  })
}
